import React from 'react';
import { Card, Empty, Flex, Pagination, Spin, Typography, theme } from '@/shared/antd-imports';

export interface MobileCardListPagination {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
}

interface MobileCardListProps<T> {
  items: T[];
  rowKey: (item: T) => string;
  /** Main line (bold). */
  renderTitle: (item: T) => React.ReactNode;
  /** Top-right corner, usually a status tag. */
  renderExtra?: (item: T) => React.ReactNode;
  /** Secondary lines under the title. */
  renderMeta?: (item: T) => React.ReactNode;
  /** Actions shown at the bottom of the card (buttons). */
  renderActions?: (item: T) => React.ReactNode;
  /** The whole card opens the item (click, Enter or Space). */
  onOpen?: (item: T) => void;
  getAriaLabel?: (item: T) => string;
  loading?: boolean;
  emptyText?: React.ReactNode;
  pagination?: MobileCardListPagination;
  ariaLabel?: string;
}

/**
 * ANS-57: on phones (< 576 px) lists show one card per row instead of a wide table, so nothing scrolls sideways.
 * Cards that open an item are keyboard reachable (Tab, Enter / Space) and announce themselves as buttons.
 */
export function MobileCardList<T>({
  items,
  rowKey,
  renderTitle,
  renderExtra,
  renderMeta,
  renderActions,
  onOpen,
  getAriaLabel,
  loading = false,
  emptyText,
  pagination,
  ariaLabel,
}: MobileCardListProps<T>) {
  const { token } = theme.useToken();

  if (!loading && items.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  }

  return (
    <Spin spinning={loading}>
      <Flex vertical gap={8} role="list" aria-label={ariaLabel} style={{ minWidth: 0 }}>
        {items.map(item => {
          const open = onOpen ? () => onOpen(item) : undefined;
          return (
            <div role="listitem" key={rowKey(item)} style={{ minWidth: 0 }}>
              <Card
                size="small"
                hoverable={!!open}
                role={open ? 'button' : undefined}
                tabIndex={open ? 0 : undefined}
                aria-label={open && getAriaLabel ? getAriaLabel(item) : undefined}
                onClick={open}
                onKeyDown={
                  open
                    ? event => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          open();
                        }
                      }
                    : undefined
                }
                style={{ cursor: open ? 'pointer' : undefined, borderColor: token.colorBorderSecondary }}
                styles={{ body: { padding: 12 } }}
              >
                <Flex vertical gap={6} style={{ minWidth: 0 }}>
                  <Flex justify="space-between" align="flex-start" gap={8}>
                    <Typography.Text strong style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      {renderTitle(item)}
                    </Typography.Text>
                    {renderExtra && <div style={{ flexShrink: 0 }}>{renderExtra(item)}</div>}
                  </Flex>
                  {renderMeta && (
                    <div style={{ minWidth: 0, overflowWrap: 'anywhere', color: token.colorTextSecondary, fontSize: 13 }}>
                      {renderMeta(item)}
                    </div>
                  )}
                  {renderActions && (
                    <Flex
                      gap={8}
                      wrap="wrap"
                      onClick={event => event.stopPropagation()}
                      onKeyDown={event => event.stopPropagation()}
                    >
                      {renderActions(item)}
                    </Flex>
                  )}
                </Flex>
              </Card>
            </div>
          );
        })}
      </Flex>
      {pagination && pagination.total > pagination.pageSize && (
        <Flex justify="center" style={{ marginTop: 12 }}>
          <Pagination
            simple
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
          />
        </Flex>
      )}
    </Spin>
  );
}

export default MobileCardList;
